import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories } from '@/data/categories';
import { MAIN_SITE_URL, ARTICLE_SLUG } from '@/data/site';
import { isSupportedLanguage } from '@/data/languages';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const lang = isSupportedLanguage(firstSegment) ? firstSegment : 'en';

  function goToCategories() {
    setMobileOpen(false);
    const isHome = location.pathname.split('/').filter(Boolean).length <= 1;
    if (isHome) {
      document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/${lang}`);
      window.setTimeout(() => {
        document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  }

  const linkClass = 'whitespace-nowrap text-sm font-semibold text-foreground-900 transition-colors hover:text-primary-700';

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-background-200 bg-background-50">
      <nav className="flex items-center justify-between px-4 py-3 md:px-6 md:py-4">
        <Link to={`/${lang}`} className="flex items-center gap-2.5 text-foreground-950">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500">
            <i className="ri-table-2 text-lg leading-none text-white"></i>
          </span>
          <span className="whitespace-nowrap font-heading text-lg font-semibold">{t('brand')}</span>
        </Link>

        <div className="hidden items-center gap-7 md:flex">
          <Link to={`/${lang}`} className={linkClass}>
            {t('nav.home')}
          </Link>
          <Link to={`/${lang}/${ARTICLE_SLUG}`} className={linkClass}>
            {t('nav.guide')}
          </Link>
          <button type="button" onClick={goToCategories} className={`${linkClass} cursor-pointer`}>
            {t('nav.categories')}
          </button>
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher solid />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            {t('buttons.buyNow')}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-950 md:hidden cursor-pointer"
          aria-label="Menu"
        >
          <i className={`text-2xl leading-none ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-6 pt-3 md:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to={`/${lang}`}
              className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-background-100"
            >
              {t('nav.home')}
            </Link>
            <Link
              to={`/${lang}/${ARTICLE_SLUG}`}
              className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-background-100"
            >
              {t('nav.guide')}
            </Link>
            <button
              type="button"
              onClick={goToCategories}
              className="rounded-md px-3 py-2.5 text-left text-sm font-semibold text-foreground-950 hover:bg-background-100 cursor-pointer"
            >
              {t('nav.categories')}
            </button>
          </div>

          <div className="mt-4">
            <p className="px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">
              {t('footer.categoriesTitle')}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {categories.map((category) => (
                <a
                  key={category.key}
                  href={category.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  <img
                    src={category.image}
                    alt={t(category.key)}
                    className="h-7 w-7 rounded object-cover"
                  />
                  <span className="truncate">{t(category.key)}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <LanguageSwitcher solid />
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              {t('buttons.buyNow')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}